import { useEffect, useRef, useState } from "react"
import { unlockGuestGuidebook } from "../api"

interface Props {
  open: boolean
  slug: string
  guestToken: string
  onClose: () => void
  onSuccess: () => void
}

const LockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
)

export function PinUnlockModal({ open, slug, guestToken, onClose, onSuccess }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [digits, setDigits] = useState(["", "", "", ""])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open) {
      dialog.showModal()
      setTimeout(() => inputRefs[0].current?.focus(), 50)
    } else {
      dialog.close()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(pin: string) {
    if (pin.length !== 4 || loading) return
    setLoading(true)
    setError("")
    try {
      const res = await unlockGuestGuidebook(slug, guestToken, pin)
      if (res.ok && res.unlocked) {
        setDigits(["", "", "", ""])
        onSuccess()
      } else {
        setError("Incorrect code. Please try again.")
        setDigits(["", "", "", ""])
        setTimeout(() => inputRefs[0].current?.focus(), 50)
      }
    } catch {
      setError("Something went wrong. Please try again.")
    }
    setLoading(false)
  }

  function handleChange(index: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1)
    const next = [...digits]
    next[index] = digit
    setDigits(next)
    setError("")

    if (digit && index < 3) {
      inputRefs[index + 1].current?.focus()
    }

    if (digit && index === 3) {
      const pin = [...next.slice(0, 3), digit].join("")
      if (pin.length === 4) void handleSubmit(pin)
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs[index - 1].current?.focus()
    }
  }

  function handleClose() {
    setDigits(["", "", "", ""])
    setError("")
    onClose()
  }

  const pin = digits.join("")

  return (
    <dialog ref={dialogRef} className="gc-pin-dialog" onClose={handleClose}>
      <div className="gc-pin-dialog__inner">
        <div className="gc-pin-dialog__icon"><LockIcon /></div>
        <div className="gc-pin-dialog__title">Enter access code</div>
        <div className="gc-pin-dialog__desc">
          Enter the 4-digit code from your booking confirmation to unlock stay details.
        </div>
        <div className="gc-pin-inputs">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={inputRefs[i]}
              className={`gc-pin-input${error ? " gc-pin-input--error" : ""}`}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={d}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              aria-label={`Digit ${i + 1} of 4`}
            />
          ))}
        </div>
        <div className="gc-pin-error">{error}</div>
        <div className="gc-pin-hint">Use the 4-digit code from your booking</div>
        <button
          className="gc-pin-submit"
          onClick={() => void handleSubmit(pin)}
          disabled={pin.length !== 4 || loading}
          type="button"
        >
          {loading ? "Verifying..." : "Unlock"}
        </button>
        <button className="gc-pin-cancel" onClick={handleClose} type="button">
          Cancel
        </button>
      </div>
    </dialog>
  )
}
